import { AxiosInstance } from 'axios';
import {
  createAxiosInstance,
  attachAuthInterceptors,
} from '@/application/helpers/APIRequest';
import { buildQueryParams } from '../helpers/utility';

const financeAPI: AxiosInstance = createAxiosInstance({
  baseUrlKey: 'FINANCE_API_BASE_URL',
});
attachAuthInterceptors(financeAPI);

const cryptoPaymentAPI: AxiosInstance = createAxiosInstance({
  baseUrlKey: 'CRYPTO_PAYMENT_API_BASE_URL',
});
attachAuthInterceptors(cryptoPaymentAPI);

export interface GetTransactionsDataRequest {
  page?: number;
  limit?: number;
  transaction_no?: string;
  business_name?: string;
  username?: string;
  related_username?: string;
  performed_by?: string;
  transaction_type?: string;
  payment_method?: string;
  status?: string;
  created_at_from?: string;
  created_at_to?: string;
  completed_at_from?: string;
  completed_at_to?: string;
  inet_ref?: string;
}

export interface TransactionData {
  id: string;
  reference_id: string;
  transaction_no: string;
  username: string;
  related_username: string;
  business_name: string;
  transaction_type: string;
  status: string;
  wallet_id: string;
  amount: number;
  fee: number;
  bonus: number;
  note: string;
  credit_before: number;
  credit_after: number;
  description: string;
  slip_image_url: string;
  related_user_id: string;
  created_at: string;
  fee_type: string;
  fee_value: number;
  bonus_type: string;
  bonus_value: number;
  completed_at: string;
  topup_method: string;
  transaction_method: string;
  payment_method: string;
  failed_reason: string;
  failed_reason_display?: string;
  description_display?: string;
  original_transaction_no?: string;
  original_reference_id?: string | null;
  linked_order_no?: string | null;
  linked_order_status?: string | null;
  linked_game_name?: string | null;
  linked_package_name?: string | null;
  metadata: TransactionMetadata | null;
}

export interface TransactionMetadata {
  price: number;
  serial_code_name: string;
  serial_code_id: string;
  business_name: string;
  created_by_username: string;
  game_id: string;
  game_name: string;
  product_type: string;
  package_name: string;
  tx_hash: string;
  crypto_currency: number;
  exchange_rate: number;
  refund_reason?: string;
  inet_order_id?: string;
  payment_ref?: string;
}

export interface GetTransactionsDataResponse {
  success: boolean;
  data?: {
    transactions: TransactionData[];
    total: number;
    limit: number;
    page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  message?: string;
}

/**
 * Fetches paginated transaction list with filters.
 * @param params - Pagination and filters (transaction_no, username, status, etc.)
 * @returns Promise with transactions array and pagination info
 */
export const getTransactionsData = async (
  params: GetTransactionsDataRequest
): Promise<GetTransactionsDataResponse> => {
  try {
    const {
      page = 1,
      limit = 10,
      transaction_no,
      business_name,
      username,
      related_username,
      performed_by,
      transaction_type,
      payment_method,
      status,
      created_at_from,
      created_at_to,
      completed_at_from,
      completed_at_to,
      inet_ref,
    } = params;
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());
    if (transaction_no) {
      queryParams.append('transaction_no', transaction_no);
    }
    if (business_name) {
      queryParams.append('business_name', business_name);
    }
    if (username) {
      queryParams.append('username', username);
    }
    if (related_username) {
      queryParams.append('related_username', related_username);
    }
    if (performed_by) {
      queryParams.append('performed_by', performed_by);
    }
    if (transaction_type) {
      queryParams.append('transaction_type', transaction_type.toUpperCase());
    }
    if (payment_method) {
      queryParams.append('payment_method', payment_method.toUpperCase());
    }
    if (status) {
      queryParams.append('status', status.toUpperCase());
    }
    if (created_at_from) {
      queryParams.append('created_at_from', created_at_from);
    }
    if (created_at_to) {
      queryParams.append('created_at_to', created_at_to);
    }
    if (completed_at_from) {
      queryParams.append('completed_at_from', completed_at_from);
    }
    if (completed_at_to) {
      queryParams.append('completed_at_to', completed_at_to);
    }
    if (inet_ref) {
      queryParams.append('inet_ref', inet_ref);
    }

    const response = await financeAPI.get(
      `/admin/transactions?${queryParams.toString()}`
    );
    return {
      success: true,
      data: {
        transactions: response.data.data,
        total: response.data.total,
        limit: response.data.limit,
        page: response.data.page,
        total_pages: response.data.total_pages,
        has_next: response.data.has_next || false,
        has_previous: response.data.has_previous || false,
      },
      message: response.data.message || 'Financial data fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch financial data',
    };
  }
};

export interface GetTransactionDetailsResponse {
  success: boolean;
  data?: TransactionData;
  message?: string;
}

/**
 * Fetches single transaction details by ID.
 * @param id - Transaction ID
 * @returns Promise with transaction data
 */
export const getTransactionDetails = async (
  id: string
): Promise<GetTransactionDetailsResponse> => {
  try {
    const response = await financeAPI.get(`/admin/transactions/${id}`);
    return {
      success: true,
      data: response.data,
      message:
        response.data.message || 'Transaction details fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch transaction details',
    };
  }
};

export interface GetSMSWebhookLogsRequest {
  page?: number;
  limit?: number;
  matched?: boolean;
  created_at_from?: string;
  created_at_to?: string;
}

export interface SMSWebhookLogData {
  id: string;
  sms_body: string;
  sms_hash: string;
  amount: number;
  matched: boolean;
  request_id?: string;
  ip_address: string;
  error?: string;
  created_at: string;
}

export interface GetSMSWebhookLogsResponse {
  success: boolean;
  data?: {
    logs: SMSWebhookLogData[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
  message?: string;
}

export const getSMSWebhookLogs = async (
  params: GetSMSWebhookLogsRequest
): Promise<GetSMSWebhookLogsResponse> => {
  try {
    const {
      page = 1,
      limit = 10,
      matched,
      created_at_from,
      created_at_to,
    } = params;
    const queryParams = new URLSearchParams();

    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    if (typeof matched === 'boolean') {
      queryParams.append('matched', matched ? 'true' : 'false');
    }

    if (created_at_from) {
      queryParams.append('created_at_from', created_at_from);
    }

    if (created_at_to) {
      queryParams.append('created_at_to', created_at_to);
    }

    const response = await financeAPI.get(
      `/admin/sms-webhook-logs?${queryParams.toString()}`
    );

    return {
      success: true,
      data: {
        logs: response.data.data || [],
        total: response.data.total || 0,
        page: response.data.page || page,
        limit: response.data.limit || limit,
        total_pages: response.data.total_pages || 1,
      },
      message: response.data.message || 'SMS webhook logs fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch SMS webhook logs',
    };
  }
};

export interface GetQRRequestsRequest {
  page?: number;
  limit?: number;
  status?: 'PENDING' | 'MATCHED' | 'EXPIRED' | 'CANCELLED';
  user_id?: string;
  business_name?: string;
  created_at_from?: string;
  created_at_to?: string;
}

export interface QRRequestData {
  id: string;
  wallet_id: string;
  user_id: string;
  username: string;
  business_name: string;
  base_amount: number;
  total_amount: number;
  decimal_suffix: number;
  status: 'PENDING' | 'MATCHED' | 'EXPIRED' | 'CANCELLED';
  expires_at: string;
  matched_at?: string;
  transaction_id?: string;
  transaction_no?: string;
  created_at: string;
}

export interface QRRequestMatchedSMSData {
  id: string;
  amount: number;
  matched_at?: string;
}

export interface QRRequestDetailData extends QRRequestData {
  transaction_no?: string;
  credit_before?: number;
  credit_after?: number;
  matched_sms?: QRRequestMatchedSMSData;
}

export interface GetQRRequestsResponse {
  success: boolean;
  data?: {
    requests: QRRequestData[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
  message?: string;
}

export interface GetQRRequestByIdResponse {
  success: boolean;
  data?: QRRequestDetailData;
  message?: string;
}
export const getQRRequests = async (
  params: GetQRRequestsRequest
): Promise<GetQRRequestsResponse> => {
  try {
    const {
      page = 1,
      limit = 10,
      status,
      user_id,
      business_name,
      created_at_from,
      created_at_to,
    } = params;
    const queryParams = new URLSearchParams();

    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    if (status) {
      queryParams.append('status', status);
    }

    if (user_id) {
      queryParams.append('user_id', user_id);
    }

    if (business_name) {
      queryParams.append('business_name', business_name);
    }

    if (created_at_from) {
      queryParams.append('created_at_from', created_at_from);
    }

    if (created_at_to) {
      queryParams.append('created_at_to', created_at_to);
    }

    const response = await financeAPI.get(
      `/admin/qr-requests?${queryParams.toString()}`
    );

    return {
      success: true,
      data: {
        requests: response.data.data || [],
        total: response.data.total || 0,
        page: response.data.page || page,
        limit: response.data.limit || limit,
        total_pages: response.data.total_pages || 1,
      },
      message: response.data.message || 'QR requests fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch QR requests',
    };
  }
};

export const getQRRequestById = async (
  requestId: string
): Promise<GetQRRequestByIdResponse> => {
  try {
    const response = await financeAPI.get(`/admin/qr-requests/${requestId}`);

    return {
      success: true,
      data: response.data.data || response.data,
      message: response.data.message || 'QR request fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch QR request',
    };
  }
};

export interface TransactionCount {
  top_up: number;
  deduct: number;
  total?: number;
}

export interface MoneyFlow {
  total_transfer_baht: number;
  total_fee_baht: number;
  net_credit_added: number;
}

export interface SystemCredit {
  total_credit: number;
}

export interface GetCreditAndWalletDashboardStatResponse {
  success: boolean;
  data?: {
    transaction_count: TransactionCount;
    money_flow: MoneyFlow;
    system_credit: SystemCredit;
  };
  message?: string;
}

/**
 * Fetches credit and wallet dashboard stats (transaction count, money flow, system credit).
 * @param params - Optional filters
 * @returns Promise with dashboard stat data
 */
export const getCreditAndWalletDashboardStat = async (
  params?: GetTransactionsDataRequest
): Promise<GetCreditAndWalletDashboardStatResponse> => {
  try {
    const queryParams = new URLSearchParams();

    if (params) {
      const {
        transaction_no,
        business_name,
        username,
        related_username,
        performed_by,
        transaction_type,
        status,
        // completed_at_from,
        // completed_at_to,
        // business_owner_username,
        // business_owner_firstname,
        // business_owner_lastname,
      } = params;

      if (transaction_no) queryParams.append('transaction_no', transaction_no);

      if (business_name) queryParams.append('business_name', business_name);

      if (username) queryParams.append('username', username);

      if (related_username)
        queryParams.append('related_username', related_username);

      if (performed_by) queryParams.append('performed_by', performed_by);

      if (transaction_type)
        queryParams.append('transaction_type', transaction_type.toUpperCase());

      if (status) queryParams.append('status', status.toUpperCase());

      // if (completed_at_from)
      //   queryParams.append('completed_at_from', completed_at_from);

      // if (completed_at_to)
      //   queryParams.append('completed_at_to', completed_at_to);

      // if (business_owner_username)
      //   queryParams.append('business_owner_username', business_owner_username);

      // if (business_owner_firstname)
      //   queryParams.append(
      //     'business_owner_firstname',
      //     business_owner_firstname
      //   );

      // if (business_owner_lastname)
      //   queryParams.append('business_owner_lastname', business_owner_lastname);
    }

    const response = await financeAPI.get(
      `/admin/dashboard/stats${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`
    );
    return {
      success: true,
      data: {
        transaction_count: response.data.transaction_count,
        money_flow: response.data.money_flow,
        system_credit: response.data.system_credit,
      },
      message:
        response.data.message ||
        'Credit and wallet dashboard stat fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch credit and wallet dashboard stat',
    };
  }
};

export interface CreditTopupRequest {
  business_profile_id: string;
  amount: number;
  slip_file?: File;
  note: string;
  verify_slip: boolean;
}

export interface CreditTopupResponse {
  success: boolean;
  data?: {
    reference_id: string;
    user_id: string;
    credit_before: number;
    credit_after: number;
    slip_image_url: string;
    fee_amount: number;
    note: string;
    amount: number;
  };
  message?: string;
}

/**
 * Performs credit topup for a user (staff action).
 * @param data - Topup request (username, amount, slip_file, note, verify_slip)
 * @returns Promise with topup result
 */
export const creditTopup = async (
  data: CreditTopupRequest
): Promise<CreditTopupResponse> => {
  try {
    const formData = new FormData();
    formData.append('business_profile_id', data.business_profile_id);
    formData.append('amount', data.amount.toString());
    formData.append('note', data.note);
    formData.append('verify_slip', data.verify_slip ? 'true' : 'false');
    if (data.slip_file) {
      formData.append('slip_file', data.slip_file);
    }

    const response = await financeAPI.post('/admin/credits/topup', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const resData = response.data.data || response.data;
    return {
      success: true,
      data: {
        reference_id: resData.transaction_no,
        user_id: resData.user_id,
        credit_before: resData.credit_before,
        credit_after: resData.credit_after,
        slip_image_url: resData.slip_image_url,
        fee_amount: resData.fee_amount || resData.fee,
        note: resData.note,
        amount: resData.amount,
      },
      message: response.data.message || 'Credit topup successful',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to credit topup',
    };
  }
};

export interface CreditBalanceResponse {
  user_id: string;
  username: string;
  credit: number;
  wallet_status: string;
}

/**
 * Fetches credit balance for a user.
 * @param username - Username to query
 * @returns Promise with balance data
 */
export const getCreditBalance = (business_profile_id: string) => {
  return financeAPI.get<CreditBalanceResponse>(
    `/admin/credits/business/${business_profile_id}/balance`
  );
};

export interface DeductCreditRequest {
  business_profile_id: string;
  amount: number;
  note: string;
}

export interface DeductCreditResponse {
  success: boolean;
  data?: {
    reference_id: string;
    transaction_no: string;
    user_id: string;
    credit_before: number;
    credit_after: number;
    note: string;
    amount: number;
  };
  message?: string;
}

/**
 * Deducts credit from a user's wallet (staff action).
 * @param data - Deduct request (username, amount, note)
 * @returns Promise with deduct result
 */
export const deductCredit = async (
  data: DeductCreditRequest
): Promise<DeductCreditResponse> => {
  try {
    const response = await financeAPI.post('/admin/credits/deduct', {
      business_profile_id: data.business_profile_id,
      amount: data.amount,
      note: data.note,
    });
    const resData = response.data.data || response.data;
    return {
      success: true,
      data: {
        reference_id: resData.reference_id,
        transaction_no: resData.transaction_no,
        user_id: resData.user_id,
        credit_before: resData.credit_before,
        credit_after: resData.credit_after,
        note: resData.note,
        amount: resData.amount,
      },
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to deduct credit',
    };
  }
};
export interface MyCreditResponse {
  success: boolean;
  data?: any;
  message?: string;
}
/**
 * Fetches current user's wallet/credit info.
 * @returns Promise with wallet data
 */
export const GetMyCredit = async (): Promise<MyCreditResponse> => {
  try {
    const response = await financeAPI.get('/wallets/me');
    return {
      success: true,
      data: response.data,
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to deduct credit',
    };
  }
};

// cryptoPaymentAPI is now created using createAPI and included in apis array above

export interface CheckCryptoTransactionRequest {
  username: string;
  transaction_hash: string;
  reason: string;
}

export interface CheckCryptoTransactionResponse {
  success: boolean;
  data?: any;
  message?: string;
  code?: string;
}

/**
 * Checks a crypto transaction for verification.
 * @param data - Transaction hash, username, reason
 * @returns Promise with check result
 */
export const checkCryptoTransaction = async (
  data: CheckCryptoTransactionRequest
): Promise<CheckCryptoTransactionResponse> => {
  try {
    const response = await cryptoPaymentAPI.post(
      '/admin/crypto-transactions/check',
      data
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to check crypto transaction',
      code: error.response?.data?.code,
    };
  }
};

export interface ConfirmCryptoTransactionRequest {
  transaction_hash: string;
  sender_wallet_address: string;
  receiver_wallet: string;
  raw_value: string;
  decimals: number;
  amount: number;
  exchange_rate_thb: number;
  amount_thb: number;
  credit_received: number;
  token_address: string | null;
  symbol: string;
  receiver_username: string;
  receiver_user_id?: string;
  block_number: number;
  confirmations: number;
  gas_used: number;
  block_timestamp: string;
  reason: string;
}

export interface ConfirmCryptoTransactionResponse {
  success: boolean;
  data?: any;
  message?: string;
  code?: string;
}

/**
 * Confirms a crypto transaction and credits the user.
 * @param data - Full transaction details from blockchain
 * @returns Promise with confirm result
 */
export const confirmCryptoTransaction = async (
  data: ConfirmCryptoTransactionRequest
): Promise<ConfirmCryptoTransactionResponse> => {
  try {
    const response = await cryptoPaymentAPI.post(
      '/admin/crypto-transactions/confirm',
      data
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to confirm crypto transaction',
      code: error.response?.data?.code,
    };
  }
};

/**
 * Exports admin credit transactions as CSV or XLSX file.
 * @param params - Filter params for export
 * @param format - 'csv' or 'xlsx'
 * @returns Promise with Blob file data
 */
export const exportAdminCreditTransactionFile = async (
  params: Record<string, any>,
  format: 'csv' | 'xlsx'
): Promise<Blob> => {
  const queryParams = buildQueryParams({ ...params, format });
  const response = await financeAPI.get('/admin/transactions/export', {
    params: queryParams,
    responseType: 'blob',
  });
  return response.data;
};

export interface ExportSMSWebhookLogsParams {
  matched?: boolean;
  created_at_from?: string;
  created_at_to?: string;
}

export const exportSMSWebhookLogsFile = async (
  params: ExportSMSWebhookLogsParams,
  format: 'csv' | 'xlsx'
): Promise<Blob> => {
  const queryParams = buildQueryParams({ ...params, format });
  const response = await financeAPI.get('/admin/sms-webhook-logs/export', {
    params: queryParams,
    responseType: 'blob',
    skipNotFoundNavigation: true,
  });
  return response.data;
};

export interface ExportQRRequestsParams {
  status?: string;
  username?: string;
  business_name?: string;
  created_at_from?: string;
  created_at_to?: string;
}

export const exportQRRequestsFile = async (
  params: ExportQRRequestsParams,
  format: 'csv' | 'xlsx'
): Promise<Blob> => {
  const queryParams = buildQueryParams({ ...params, format });
  const response = await financeAPI.get('/admin/qr-requests/export', {
    params: queryParams,
    responseType: 'blob',
    skipNotFoundNavigation: true,
  });
  return response.data;
};

export interface BusinessOption {
  id: string;
  business_name: string;
}

export interface GetAllBusinessesResponse {
  success: boolean;
  data?: BusinessOption[];
  message?: string;
}

export const getAllBusinesses = async (): Promise<GetAllBusinessesResponse> => {
  try {
    const response = await financeAPI.get('/admin/businesses');
    return {
      success: true,
      data: response.data.data || [],
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch businesses',
    };
  }
};

// ============================================================================
// Pending Review Slip TopUp
// ============================================================================

export interface PendingReviewTransaction {
  id: string;
  transaction_no: string;
  user_id: string;
  username: string;
  business_name?: string;
  amount: number;
  status: string;
  payment_method?: string;
  slip_image_url?: string;
  note?: string;
  failed_reason?: string;
  created_at: string;
}

export interface PendingReviewListResponse {
  success: boolean;
  data?: {
    data: PendingReviewTransaction[];
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
  message?: string;
}

export const getPendingReviewTopUps = async (params: {
  page: number;
  limit: number;
  business_name?: string;
  username?: string;
  created_at_from?: string;
  created_at_to?: string;
}): Promise<PendingReviewListResponse> => {
  try {
    const queryParams = buildQueryParams(params);
    const response = await financeAPI.get(`/admin/pending-review-topups?${queryParams.toString()}`);
    return { success: true, data: response.data };
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to fetch pending review topups',
    };
  }
};

export const approveSlipTopUp = async (
  id: string,
  note?: string
): Promise<{ success: boolean; data?: PendingReviewTransaction; message?: string }> => {
  try {
    const response = await financeAPI.post(`/admin/pending-review-topups/${id}/approve`, { note });
    return { success: true, data: response.data?.data };
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to approve topup',
    };
  }
};

export const rejectSlipTopUp = async (
  id: string,
  reason: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    await financeAPI.post(`/admin/pending-review-topups/${id}/reject`, { reason });
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to reject topup',
    };
  }
};

export default financeAPI;
